import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Table,
  Button,
  Space,
  Tag,
  Tooltip,
  Typography,
  Modal,
  Empty,
  Switch
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  RocketOutlined,
  CalendarOutlined,
  TeamOutlined,
  GlobalOutlined,
  AppstoreOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { useAppDispatch, useAppSelector } from 'store';
import {
  fetchGlobalCampaigns,
  deleteGlobalCampaign,
  updateGlobalCampaign,
  selectGlobalCampaigns,
  selectGlobalCampaignsLoading,
} from 'store/slices/globalCampaigns.slice';
import {
  fetchApplications,
  selectApplications,
} from 'store/slices/applications.slice';
import { Campaign, PushType } from 'types/push.types';
import { formatters } from 'utils/formatters';
import { notify } from 'utils/helpers';
import { ROUTES } from 'config/routes';
import { resolve } from 'utils/resolve';

const { confirm } = Modal;
const { Text, Title } = Typography;

const getPushTypeTag = (type: PushType) => {
  const config = {
    [PushType.REGULAR]: { color: 'blue', label: 'Обычная' },
    [PushType.EVENT]: { color: 'purple', label: 'Событийная' },
    [PushType.SPECIAL]: { color: 'gold', label: 'Специальная' },
  };
  const { color, label } = config[type] || { color: 'default', label: type };
  return <Tag color={color}>{label}</Tag>;
};

const GlobalCampaignsListPage: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const campaigns = useAppSelector(selectGlobalCampaigns);
  const applications = useAppSelector(selectApplications);
  const loading = useAppSelector(selectGlobalCampaignsLoading);

  useEffect(() => {
    dispatch(fetchGlobalCampaigns());
    if (!applications || applications.length === 0) {
      dispatch(fetchApplications({ limit: 1000 }));
    }
  }, [dispatch]);

  const getAppName = (appId: number): string => {
    const app = applications?.find(a => a.id === appId);
    return app ? app.name : `App #${appId}`;
  };

  const handleToggleActive = async (campaign: Campaign) => {
    try {
      await dispatch(
        updateGlobalCampaign({
          id: campaign.id,
          data: { is_active: !campaign.is_active },
        }),
      ).unwrap();
      notify.success(
        campaign.is_active
          ? 'Кампания деактивирована'
          : 'Кампания активирована',
      );
    } catch (error: any) {
      notify.error(error.message || error || 'Ошибка при обновлении');
    }
  };

  const handleDelete = (campaign: Campaign) => {
    confirm({
      title: 'Удалить глобальную кампанию?',
      content: `Удалить "${campaign.name}"? Уже созданные push-сообщения сохранятся в истории, но связь с кампанией пропадёт.`,
      okText: 'Удалить',
      okType: 'danger',
      cancelText: 'Отмена',
      onOk: async () => {
        try {
          await dispatch(deleteGlobalCampaign(campaign.id)).unwrap();
          notify.success('Глобальная кампания удалена');
        } catch (error: any) {
          notify.error(error.message || error || 'Ошибка при удалении');
        }
      },
    });
  };

  const columns: ColumnsType<Campaign> = [
    {
      title: 'Название',
      dataIndex: 'name',
      key: 'name',
      width: 250,
      render: (name: string, record) => (
        <div>
          <Space size={6}>
            <GlobalOutlined style={{ color: 'var(--accent-purple)' }} />
            <Text strong style={{ fontSize: 14 }}>{name}</Text>
          </Space>
          {record.description && (
            <div>
              <Text type="secondary" style={{ fontSize: 12 }}>
                {formatters.truncate(record.description, 80)}
              </Text>
            </div>
          )}
        </div>
      ),
    },
    {
      title: 'Тип',
      dataIndex: 'push_type',
      key: 'push_type',
      width: 120,
      render: (type: PushType) => getPushTypeTag(type),
    },
    {
      title: 'Target apps',
      key: 'target_apps',
      width: 280,
      render: (_, record) => {
        const ids = record.target_app_ids || [];
        if (ids.length === 0) {
          return <Tag color="default">Не указано</Tag>;
        }
        const visible = ids.slice(0, 3);
        const rest = ids.length - visible.length;
        return (
          <Space size={4} wrap>
            {visible.map(id => (
              <Tag key={id} icon={<AppstoreOutlined />} color="purple">
                {getAppName(id)}
              </Tag>
            ))}
            {rest > 0 && (
              <Tooltip
                title={ids.slice(3).map(getAppName).join(', ')}
              >
                <Tag color="purple">+{rest}</Tag>
              </Tooltip>
            )}
          </Space>
        );
      },
    },
    {
      title: 'Push',
      key: 'push_count',
      width: 80,
      align: 'center',
      render: (_, record) => {
        const count = record.pushes_count ?? record.pushes?.length ?? 0;
        return (
          <Tag color={count > 0 ? 'green' : 'default'}>{count}</Tag>
        );
      },
    },
    {
      title: 'Период',
      key: 'period',
      width: 200,
      render: (_, record) => {
        const start = formatters.date(record.start_date);
        const end = record.end_date
          ? formatters.date(record.end_date)
          : 'Бессрочно';
        return (
          <Space size={4}>
            <CalendarOutlined style={{ color: 'var(--text-muted)' }} />
            <Text style={{ fontSize: 13 }}>{start} — {end}</Text>
          </Space>
        );
      },
    },
    {
      title: 'Сегмент',
      key: 'segment',
      width: 130,
      render: (_, record) => {
        const segmentId = resolve<number>(record.segment_id);
        if (segmentId) {
          return (
            <Tag icon={<TeamOutlined />} color="blue">
              Сегмент #{segmentId}
            </Tag>
          );
        }
        return <Tag icon={<TeamOutlined />}>Все пользователи</Tag>;
      },
    },
    {
      title: 'Статус',
      dataIndex: 'is_active',
      key: 'is_active',
      width: 100,
      align: 'center',
      render: (isActive: boolean, record) => (
        <Tooltip title={isActive ? 'Деактивировать' : 'Активировать'}>
          <Switch
            checked={isActive}
            onChange={() => handleToggleActive(record)}
            checkedChildren={<CheckCircleOutlined />}
            unCheckedChildren={<CloseCircleOutlined />}
          />
        </Tooltip>
      ),
    },
    {
      title: 'Действия',
      key: 'actions',
      width: 100,
      fixed: 'right',
      render: (_, record) => (
        <Space size="small">
          <Tooltip title="Редактировать">
            <Button
              type="text"
              icon={<EditOutlined />}
              onClick={() =>
                navigate(
                  ROUTES.GLOBAL_CAMPAIGN_EDIT.replace(':id', String(record.id)),
                )
              }
            />
          </Tooltip>
          <Tooltip title="Удалить">
            <Button
              type="text"
              danger
              icon={<DeleteOutlined />}
              onClick={() => handleDelete(record)}
            />
          </Tooltip>
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      <div
        style={{
          marginBottom: 16,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <div>
          <Space>
            <GlobalOutlined style={{ color: 'var(--accent-purple)', fontSize: 20 }} />
            <Title level={3} style={{ margin: 0, color: 'var(--accent-purple)' }}>
              Глобальные кампании
            </Title>
          </Space>
          <div>
            <Text type="secondary">
              Кампании, которые отправляются сразу в несколько приложений
            </Text>
          </div>
        </div>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={() => navigate(ROUTES.GLOBAL_CAMPAIGN_CREATE)}
          size="large"
        >
          Создать глобальную кампанию
        </Button>
      </div>
      <Table
        columns={columns}
        dataSource={campaigns}
        loading={loading}
        rowKey="id"
        pagination={{
          pageSize: 20,
          showSizeChanger: true,
          showTotal: total => `Всего кампаний: ${total}`,
        }}
        locale={{
          emptyText: (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description={
                <Space direction="vertical">
                  <Text>Глобальные кампании не созданы</Text>
                  <Text type="secondary">
                    Создайте первую кампанию для одновременной рассылки в несколько приложений
                  </Text>
                </Space>
              }
            >
              <RocketOutlined style={{ fontSize: 36, color: 'var(--text-muted)' }} />
            </Empty>
          ),
        }}
        scroll={{ x: 1300 }}
      />
    </div>
  );
};

export default GlobalCampaignsListPage;